import { createContext, useContext, useEffect, useState } from "react";

import { ThemeContextType } from "./Theme.types.tsx";

import { getAppSettings, getSystemTheme } from "../utils/appSettings.tsx";
import { ThemeOptionsType, UnitSystemOptionsType } from "../components/Settings/Settings.types.tsx";

const ThemeContext = createContext<ThemeContextType | null>(null);

export const applyTheme = (theme: ThemeOptionsType) => {
  document.body.setAttribute("data-theme", theme);
};

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<ThemeOptionsType>("light");
  const [unitSystem, setUnitSystem] = useState<UnitSystemOptionsType>("Imperial");

  // Se compara en minúsculas porque en `localStorage` puede haber un `app_settings` guardado antes
  // de normalizar, con el `System` que manda el backend. Sin esto, esa sesión sigue en claro hasta
  // que algo reescriba el ajuste.
  const resolveTheme = (pref: ThemeOptionsType): ThemeOptionsType => {
    const normalized = String(pref).toLowerCase() as ThemeOptionsType;

    return normalized === "system" ? getSystemTheme() : normalized;
  };

  useEffect(() => {
    const settings = getAppSettings();
    const applied = resolveTheme(settings.theme);
    setTheme(applied);
    applyTheme(applied);
  }, []);

  const setThemePreference = (pref: ThemeOptionsType) => {
    const applied = resolveTheme(pref);
    setTheme(applied);
    applyTheme(applied);
  };

  return (
    <ThemeContext.Provider value={{ unitSystem, setUnitSystem, theme, setThemePreference }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const ctx = useContext(ThemeContext);

  if (!ctx) throw new Error("useTheme must be used inside ThemeProvider");

  return ctx;
};
